import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { refreshRoleChoices } from '../../hooks/useRoleOptions.js';
import { ApiError, api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { permissionHint } from './permissionHint.js';
import { scopeLabel } from './scopeLabel.js';

/**
 * 285 (#313 follow-up): closes the gap #313's original complaint found — creating a role was not
 * possible at all. Scope is a plain radio-style select (global vs. org, spec.md's locked decision)
 * and the permission grid mirrors `RoleDetailPage.tsx`'s own editable-role rendering exactly, so a
 * role created here looks and edits identically to `Admin`/`Supplier`/`Host` once saved (US3 parity).
 */

interface CataloguePermission {
  id: string;
  subject: string;
  action: string;
  scope: 'any' | 'own_supplier';
  description: string | null;
}

function groupBySubject(catalogue: CataloguePermission[]): Map<string, CataloguePermission[]> {
  const groups = new Map<string, CataloguePermission[]>();
  for (const perm of catalogue) {
    const list = groups.get(perm.subject) ?? [];
    list.push(perm);
    groups.set(perm.subject, list);
  }
  return groups;
}

export function RoleCreatePage() {
  const ability = useAbility();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<'global' | 'org'>('global');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [nameError, setNameError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const { data: catalogue, isLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.get<{ items: CataloguePermission[] }>('/permissions'),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post<{ id: string }>('/roles', payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.role.list });
      // 367 (FR-003): the user forms' role choices follow at once.
      refreshRoleChoices(queryClient);
      navigate(`/roles/${created.id}`);
    },
    onError: (err: unknown) => {
      if (
        err instanceof ApiError &&
        (err.code === 'RESERVED_NAME' || err.code === 'DUPLICATE_NAME')
      ) {
        setNameError(err.message);
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Failed to create role');
      }
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    setNameError('');
    if (name.trim() === '') {
      setNameError('Name is required.');
      return;
    }
    mutate({
      name: name.trim(),
      ...(description.trim() !== '' ? { description: description.trim() } : {}),
      scope,
      permission_ids: [...checked],
    });
  }

  // UI gating only — the backend enforces manage:Role on POST /roles (same gate as PATCH
  // /roles/:id/permissions).
  if (!ability.can('manage', 'Role')) return <Navigate to="/roles" replace />;

  return (
    <div>
      <PageHeader
        title="New Role"
        description="Create a role and assign it permissions from the existing catalogue."
        fallback="/roles"
      />
      <form className="max-w-2xl space-y-6" onSubmit={onSubmit} noValidate>
        <Card>
          <div className="space-y-4">
            <FormField label="Name" name="name" required error={nameError}>
              <Input
                id="name"
                name="name"
                maxLength={100}
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Description" name="description">
              <Textarea
                id="description"
                name="description"
                maxLength={1000}
                value={description}
                onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              />
            </FormField>
            <FormField
              label="Scope"
              name="scope"
              required
              hint="Global roles assign to a user account directly. Org roles assign through a supplier membership."
            >
              <Select
                id="scope"
                name="scope"
                value={scope}
                onChange={(e) =>
                  setScope((e.target as HTMLSelectElement).value as 'global' | 'org')
                }
              >
                <option value="global">Global</option>
                <option value="org">Organization</option>
              </Select>
            </FormField>
          </div>
        </Card>
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Permissions</h2>
          {isLoading ? (
            <div className="text-steel">Loading…</div>
          ) : (
            [...groupBySubject(catalogue?.items ?? []).entries()].map(([subject, perms]) => (
              <div key={subject} className="mb-6">
                <h3 className="text-sm font-semibold text-ink mb-2">{subject}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {perms.map((perm) => (
                    <label key={perm.id} className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={checked.has(perm.id)}
                        onChange={(e) => {
                          const next = new Set(checked);
                          if (e.currentTarget.checked) next.add(perm.id);
                          else next.delete(perm.id);
                          setChecked(next);
                        }}
                      />
                      <span>
                        {perm.action}
                        <span className="text-steel"> ({scopeLabel(perm.scope)})</span>
                        {permissionHint(perm.subject, perm.action) && (
                          <>
                            <span className="text-steel"> · </span>
                            <span
                              data-testid={`permission-hint-${perm.subject}-${perm.action}`}
                              className="text-steel"
                            >
                              {permissionHint(perm.subject, perm.action)}
                            </span>
                          </>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </Card>
        {submitError && <p className="text-sm text-red-600">{submitError}</p>}
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Creating…' : 'Create Role'}
        </Button>
      </form>
    </div>
  );
}
